import { BADGES_CONFIG } from '@config/badges.config'
import { accentColorConfig } from '@hooks/useAccentColor'
import { AccentColor } from '@typings/index'
import { Compass, Crown, Lightbulb, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface BadgeInfoProps {
  totalLikes: number
  accentColor: AccentColor
}

const BadgeInfo = ({ totalLikes, accentColor }: BadgeInfoProps) => {
  const { t } = useTranslation()

  const BadgeIcons = {
    Sparkles,
    Compass,
    Lightbulb,
    Crown,
  }

  return (
    <div className="space-y-6">
      <h2 className={`text-xl font-semibold ${accentColorConfig[accentColor].text} mb-4`}>
        {t('dashboard.badges_system')}
      </h2>

      <div className="grid gap-6">
        {BADGES_CONFIG.levels.map((badge) => {
          const Icon = BadgeIcons[badge.icon as keyof typeof BadgeIcons]
          const isUnlocked = totalLikes >= badge.minLikes

          return (
            <div
              key={badge.id}
              className={`p-6 rounded-lg border ${
                isUnlocked
                  ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                  : 'opacity-70  border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex gap-6">
                <div className="flex items-start space-x-4 flex-1">
                  <Icon size={24} className={isUnlocked ? badge.color : 'text-gray-400'} />
                  <div>
                    <h3
                      className={`font-semibold flex items-center gap-2 ${isUnlocked ? 'dark:text-white' : 'text-gray-500'}`}
                    >
                      {t(badge.title)}
                      {isUnlocked && (
                        <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-600">
                          {t('dashboard.unlocked')}
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t(badge.description)}</p>
                    <div className="mt-3">
                      <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                        <div
                          className={`${accentColorConfig[accentColor].primary} h-2 rounded-full transition-all`}
                          style={{
                            width: `${Math.min(100, (totalLikes / badge.minLikes) * 100)}%`,
                          }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {totalLikes} / {badge.minLikes} likes
                      </span>
                    </div>
                  </div>
                </div>

                <div className="border-l pl-6 w-1/2">
                  <p className="text-sm text-gray-600 dark:text-gray-400 italic">{t(badge.longDescription)}</p>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default BadgeInfo
